import * as shadow from '@owl-nest/shadow'

/**
 * Plausible events for the Statistiques page. Every event name and prop lives here, so the page's
 * vocabulary is one file rather than a dozen string literals across `sections/`.
 *
 * Props are kept low-cardinality on purpose: Plausible indexes every distinct value, so bounded
 * sets (four CTA positions, ten ranks, and one slug per entry in `ULULE_CATEGORIES`) stay useful where
 * project slugs or free text would not.
 *
 * Scroll depth is NOT reported here. The script variant loaded in `index.html` already emits an
 * `engagement` event carrying `sd`, the percentage of the page reached, at finer resolution than
 * anything this page could observe for itself.
 */

/**
 * Where on the page a "Je lance ma collecte" button was clicked. All four point at the same URL, so
 * the destination's own pageview cannot tell them apart — this prop is the only thing that can.
 */
export type CtaPosition = 'hero' | 'success' | 'status' | 'closing'

/** Which board a project was opened from: the all-time Top 10, or the Populaires cards. */
export type BoardId = 'all_time' | 'popular'

export type StatsTracking = {
  ctaClick: (position: CtaPosition) => void
  /**
   * `rank` is the entry's position on its board: both boards order themselves by what they show.
   * `category` names which Top 10 was on screen — a slug, or `all` for the unfiltered board — so a
   * click on a filtered board is not indistinguishable from one on the default; it is omitted
   * where there is no filter.
   */
  projectClick: (board: BoardId, rank: number, category?: string) => void
  categoryClick: (slug: string) => void
  /**
   * Category picked in the Top 10 filter, or `all` for the unfiltered board. Kept apart from
   * `categoryClick`, which reports a visit to a category page — merging them would blur two
   * different intents under one slug.
   */
  topFilterChange: (slug: string) => void
  discoverClick: () => void
  dataRequestClick: () => void
}

export function useStatsTracking(): StatsTracking {
  const track = shadow.plausible.useTracking()

  return {
    ctaClick: (position) => track({ type: 'Stats_Cta_Click', props: { position } }),
    projectClick: (board, rank, category) =>
      track({
        type: 'Stats_Project_Click',
        props: { board, rank: String(rank), ...(category === undefined ? {} : { category }) },
      }),
    categoryClick: (slug) => track({ type: 'Stats_Category_Click', props: { category: slug } }),
    topFilterChange: (slug) => track({ type: 'Stats_Top_Filter_Change', props: { category: slug } }),
    discoverClick: () => track({ type: 'Stats_Discover_Click' }),
    dataRequestClick: () => track({ type: 'Stats_Data_Request_Click' }),
  }
}
